/**
 * Gemeinsames Datenmodell für Frontend und Backend.
 *
 * Eine "Einrichtung" (Layout) ist alles, was ein Nutzer im Haus verändert:
 * Möbel, eigene Möbel, Leitungen, Anschlüsse und Raumnamen – immer nur für sich selbst.
 * Das Gebäude (Wände, Fenster, Treppe) ist für alle gleich und steckt im Frontend unter apps/web/src/engine.
 *
 * Maße und Positionen sind in Metern, Planachsen wie im Bauplan:
 *   x = von links (Garagenseite) nach rechts (Nachbar), y = von der Straße nach hinten (Garten)
 *   f = Geschoss (0 UG, 1 EG, 2 OG, 3 DG), r = Drehung in Bogenmaß, z = Höhe über Fußboden
 */
import { z } from "zod";

const num = z.number().finite();
const color = z.string().max(32);

export const ItemSchema = z.object({
  k: z.string().min(1).max(80),
  x: num,
  y: num,
  f: z.number().int().min(0).max(3),
  r: num.default(0),
  w: num.positive().max(20),
  d: num.positive().max(20),
  h: num.positive().max(20),
  c: color,
  z: num.min(0).max(10).optional(),
  lock: z.union([z.literal(1), z.boolean()]).optional(),
});
export type LayoutItem = z.infer<typeof ItemSchema>;

export const CustomPartSchema = z.object({
  s: z.enum(["box", "cyl", "sph"]),
  w: num.positive(),
  d: num.positive(),
  h: num.positive(),
  x: num,
  y: num,
  z: num,
  c: color,
});
export type CustomPart = z.infer<typeof CustomPartSchema>;

export const CustomDefSchema = z.object({
  key: z.string().startsWith("cu:").max(80),
  name: z.string().max(40),
  w: num.positive(),
  d: num.positive(),
  h: num.positive(),
  c: color,
  parts: z.array(CustomPartSchema).max(200),
});
export type CustomDef = z.infer<typeof CustomDefSchema>;

/* ---------- Häuser (Zustände) ---------- */

/** Die Gebäude-Zustände. Wände stehen im Frontend-Code (engine/variants.ts), hier nur die Eckdaten. */
export const HOUSES = [
  { key: "bestand", name: "Bestand", description: "Ist-Zustand nach den Bestandsplänen BA.01–04", furniture: false },
  { key: "planung", name: "Planung", description: "Soll-Zustand mit Durchbrüchen und Änderungen", furniture: true },
] as const;
export type HouseKey = (typeof HOUSES)[number]["key"];
export const HOUSE_KEYS = HOUSES.map((h) => h.key) as HouseKey[];
export const isHouseKey = (k: unknown): k is HouseKey => typeof k === "string" && (HOUSE_KEYS as string[]).includes(k);
export const houseInfo = (k: HouseKey) => HOUSES.find((h) => h.key === k)!;

/* ---------- Leitungen ---------- */

export const LINE_TYPES = ["strom", "wasser", "warmwasser", "abwasser", "heizung", "netzwerk"] as const;
export type LineType = (typeof LINE_TYPES)[number];
export const POINT_TYPES = ["steckdose", "schalter", "licht", "verteiler", "zapfstelle", "abfluss", "heizkoerper", "netzwerkdose"] as const;
export type PointType = (typeof POINT_TYPES)[number];

const P3 = z.tuple([num, num, num]);
export const LineSchema = z.object({
  id: z.string().min(1).max(40),
  t: z.enum(LINE_TYPES),
  f: z.number().int().min(0).max(3),
  /** Punkte [x, y, Höhe über Fußboden] in Metern */
  pts: z.array(P3).min(2).max(500),
  note: z.string().max(200).optional(),
});
export type Line = z.infer<typeof LineSchema>;

export const PointSchema = z.object({
  id: z.string().min(1).max(40),
  t: z.enum(POINT_TYPES),
  f: z.number().int().min(0).max(3),
  x: num, y: num, z: num,
  note: z.string().max(200).optional(),
});
export type Point = z.infer<typeof PointSchema>;

/* ---------- Einrichtung ---------- */

/**
 * Was ein Nutzer in einem Haus-Zustand anlegt, gehört immer genau ihm – in zwei getrennten Arten:
 *  - Einrichtung (nur Planung): Möbel und eigene Möbel
 *  - Leitungsplan: Leitungen und Anschlüsse
 * Von beiden kann jeder beliebig viele Varianten haben. Das Gebäude ist für alle gleich und steht im Code.
 */
export const LAYOUT_KINDS = ["einrichtung", "leitungen"] as const;
export type LayoutKind = (typeof LAYOUT_KINDS)[number];
export const isLayoutKind = (k: unknown): k is LayoutKind => typeof k === "string" && (LAYOUT_KINDS as readonly string[]).includes(k);

/** Inhalt einer Einrichtung bzw. eines Leitungsplans (es ist jeweils nur der passende Teil gefüllt). */
export const LayoutDataSchema = z.object({
  v: z.number().int().optional(),
  items: z.array(ItemSchema).max(2000).default([]),
  custom: z.array(CustomDefSchema).max(200).default([]),
  lines: z.array(LineSchema).max(5000).default([]),
  points: z.array(PointSchema).max(5000).default([]),
});
export type LayoutData = z.infer<typeof LayoutDataSchema>;

export const LAYOUT_VERSION = 4;
export const emptyLayout = (): LayoutData => ({ v: LAYOUT_VERSION, items: [], custom: [], lines: [], points: [] });

/** Übersicht je Haus-Zustand für die Startseite – nur die eigenen Daten des angemeldeten Nutzers. */
export interface HouseSummary {
  key: HouseKey; name: string; description: string; furniture: boolean;
  /** Anzahl Einrichtungen bzw. Leitungspläne */
  layoutCount: number; planCount: number;
  /** aus dem zuletzt geänderten Leitungsplan */
  lineCount: number; pointCount: number; updatedAt: string | null;
}

/* ---------- API ---------- */

export interface Me {
  id: string;
  username: string;
  displayName: string;
  /** login = Anmeldung in der App, dev = Entwicklungsmodus ohne Anmeldung */
  authMode: "login" | "dev";
}

export interface LayoutSummary {
  id: string;
  house: HouseKey;
  kind: LayoutKind;
  name: string;
  ownerId: string;
  ownerName: string;
  itemCount: number;
  lineCount: number;
  pointCount: number;
  version: number;
  updatedAt: string;
}

export interface Layout extends LayoutSummary {
  data: LayoutData;
}

export const CreateLayoutSchema = z.object({
  house: z.enum(["bestand", "planung"]).default("planung"),
  kind: z.enum(LAYOUT_KINDS).default("einrichtung"),
  name: z.string().trim().min(1).max(60),
  data: LayoutDataSchema.optional(),
});
export const UpdateLayoutSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  data: LayoutDataSchema.optional(),
  /** Version, auf der die Änderung basiert. Weicht sie ab, antwortet der Server mit 409. */
  version: z.number().int().min(1),
});
export type CreateLayoutInput = z.infer<typeof CreateLayoutSchema>;
export type UpdateLayoutInput = z.infer<typeof UpdateLayoutSchema>;
